/**
 * Circle CCTP V2 (Cross-Chain Transfer Protocol) configuration.
 *
 * CCTP V2 enables native USDC cross-chain transfers via burn-and-mint.
 * V2 contracts are deterministically deployed at the SAME addresses on all chains.
 *
 * IMPORTANT: These are V2 addresses (not V1). V1 addresses do NOT support
 * Ink (domain 21). V2 was verified via on-chain tracing of real Ink-bound
 * receiveMessage txs on 2026-03-29.
 *
 * V2 depositForBurn has 7 params (vs V1's 4):
 *   amount, destinationDomain, mintRecipient, burnToken,
 *   destinationCaller, maxFee, minFinalityThreshold
 *
 * Sources:
 *   - @circle-fin/provider-cctp-v2 npm package
 *   - circlefin/evm-cctp-contracts GitHub
 *   - On-chain verification via MessageTransmitterV2.localDomain()
 */

export interface CctpChainConfig {
  /** Circle CCTP domain identifier (NOT the EVM chain ID) */
  domain: number;
  /** TokenMessengerV2 contract — call depositForBurn() here */
  tokenMessenger: `0x${string}`;
  /** MessageTransmitterV2 contract — call receiveMessage() here */
  messageTransmitter: `0x${string}`;
  /** USDC token address on this chain */
  usdcAddress: `0x${string}`;
}

/**
 * CCTP V2 contracts — same addresses on ALL chains (deterministic CREATE2).
 * TokenMessengerV2: 0x28b5a0e9C621a5BadaA536219b3a228C8168cf5d
 * MessageTransmitterV2: 0x81d40f21f12a8f0e3252bccb954d722d4c464b64
 */
export const CCTP_CONFIG: Record<number, CctpChainConfig> = {
  // Ethereum — domain 0
  1: {
    domain: 0,
    tokenMessenger: '0x28b5a0e9C621a5BadaA536219b3a228C8168cf5d',
    messageTransmitter: '0x81d40f21f12a8f0e3252bccb954d722d4c464b64',
    usdcAddress: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
  },
  // Arbitrum One — domain 3
  42161: {
    domain: 3,
    tokenMessenger: '0x28b5a0e9C621a5BadaA536219b3a228C8168cf5d',
    messageTransmitter: '0x81d40f21f12a8f0e3252bccb954d722d4c464b64',
    usdcAddress: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
  },
  // Ink — domain 21
  57073: {
    domain: 21,
    tokenMessenger: '0x28b5a0e9C621a5BadaA536219b3a228C8168cf5d',
    messageTransmitter: '0x81d40f21f12a8f0e3252bccb954d722d4c464b64',
    usdcAddress: '0x2d270e6886d130d724215a266106e6832161eaed',
  },
};

/** Circle Attestation Service API base URL */
export const CCTP_ATTESTATION_API =
  'https://iris-api.circle.com/v2/attestations' as const;

/** Polling interval for attestation status (ms) */
export const CCTP_ATTESTATION_POLL_INTERVAL = 10_000; // 10s

/** Get CCTP config for a chain, throws if not supported */
export function getCctpConfig(chainId: number): CctpChainConfig {
  const config = CCTP_CONFIG[chainId];
  if (!config) {
    throw new Error(`CCTP not supported on chain ${chainId}`);
  }
  return config;
}

/** Get the CCTP destination domain for a given chain ID */
export function getCctpDomain(chainId: number): number {
  return getCctpConfig(chainId).domain;
}
